import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import { toast } from 'react-toastify';
import { getUserFromLocalStorage } from '../../utilities/user';
import { colors } from './partnerTheme';
import PartnerHelp from './partnerHelp';

const API_URL = process.env.REACT_APP_SERVER_URL || '';

const permissionDescriptions = {
    riders: { label: 'Rider Management', icon: 'nc-icon nc-delivery-fast', desc: 'View, add, edit, and manage riders' },
    financials: { label: 'Financial Management', icon: 'nc-icon nc-money-coins', desc: 'View earnings, payouts, and commission reports' },
    orders: { label: 'Order Management', icon: 'nc-icon nc-cart-simple', desc: 'View orders, assign riders, and track deliveries' },
    tracking: { label: 'Real-time Tracking', icon: 'nc-icon nc-pin-3', desc: 'View rider locations on map' },
    profile: { label: 'Profile Management', icon: 'nc-icon nc-circle-09', desc: 'Edit company profile, contact info, and bank details' },
    staff: { label: 'Staff Management', icon: 'nc-icon nc-badge', desc: 'Create, edit, and manage other staff members' },
    analytics: { label: 'Analytics & Reports', icon: 'nc-icon nc-chart-bar-32', desc: 'View dashboards and performance metrics' }
};

const CreateStaff = () => {
    const history = useHistory();
    const { id } = useParams(); // For edit mode
    const isEditMode = !!id;

    // Use agent from auth state, fallback to localStorage (matching PartnerDashboard pattern)
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();

    const token = user?.token;
    const operationMode = user?.result?.operationMode;
    const isPartnerAccount = operationMode === '3PL_PARTNER';
    const userPermissions = user?.result?.permissions || {};

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [credentialsModal, setCredentialsModal] = useState(false);
    const [tempCredentials, setTempCredentials] = useState(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Staff',
        password: '',
        permissions: {
            riders: 'none',
            financials: 'none',
            orders: 'view',
            tracking: 'view',
            profile: 'none',
            staff: 'none',
            analytics: 'view'
        }
    });

    // Check permissions
    const canManageStaff = isPartnerAccount || userPermissions?.staff === 'full';

    // Track mounted state to prevent state updates after unmount
    const isMounted = React.useRef(true);

    useEffect(() => {
        isMounted.current = true;

        // Permission filtering is now handled at route level in Admin.js
        // Users without permission won't see this page in the menu
        if (isEditMode) {
            fetchStaffDetails();
        }

        // Cleanup function to mark component as unmounted
        return () => {
            isMounted.current = false;
        };
    }, [id]);

    const fetchStaffDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/3pl-staff/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && isMounted.current) {
                const staff = response.data.data.staff;
                setFormData({
                    firstName: staff.firstName || '',
                    lastName: staff.lastName || '',
                    email: staff.email || '',
                    phone: staff.phone || '',
                    role: staff.role || 'Staff',
                    password: '', // Don't populate password in edit mode
                    permissions: staff.permissions || {
                        riders: 'none',
                        financials: 'none',
                        orders: 'view',
                        tracking: 'view',
                        profile: 'none',
                        staff: 'none',
                        analytics: 'view'
                    }
                });
            }
        } catch (err) {
            console.error('Error fetching staff:', err);
            if (isMounted.current) {
                setError(err.response?.data?.error?.message || 'Failed to load staff details');
                toast.error('Failed to load staff details');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePermissionChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            permissions: { ...prev.permissions, [key]: value }
        }));
    };

    const validateForm = () => {
        if (!formData.firstName.trim()) {
            toast.error('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            toast.error('Last name is required');
            return false;
        }
        if (!formData.email.trim()) {
            toast.error('Email is required');
            return false;
        }
        if (!isEditMode && formData.password && formData.password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error('Please enter a valid email address');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSaving(true);
            setError(null);

            const submitData = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone?.trim() || '',
                role: formData.role.trim() || 'Staff',
                permissions: formData.permissions
            };

            // Only include password if provided (for create or password reset)
            if (formData.password) {
                submitData.password = formData.password;
            }
            // Invite-based onboarding: backend emails the new staff member a
            // 48h set-password link redeemed at /3pl-reset-password. The
            // temporary-password modal only appears if that email fails.
            // The link's origin comes from server configuration — a client
            // -supplied one would let a request aim a live token elsewhere.

            let response;
            if (isEditMode) {
                response = await axios.put(`${API_URL}/api/3pl-staff/${id}`, submitData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                response = await axios.post(`${API_URL}/api/3pl-staff`, submitData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (response.data.success) {
                if (isEditMode) {
                    toast.success('Staff member updated successfully');
                    history.push('/admin/3pl-staff');
                } else {
                    // Temporary-password modal is the FALLBACK (invite email
                    // failed and the backend echoed a generated password).
                    if (response.data.data.temporaryPassword) {
                        toast.warn('Invitation email could not be sent — share the temporary password below securely.', { autoClose: 9000 });
                        setTempCredentials({
                            email: submitData.email,
                            password: response.data.data.temporaryPassword
                        });
                        setCredentialsModal(true);
                    } else if (response.data.data.inviteEmailSent) {
                        toast.success('Staff member created — invitation email sent to set their password.');
                        history.push('/admin/3pl-staff');
                    } else {
                        toast.success('Staff member created successfully');
                        history.push('/admin/3pl-staff');
                    }
                }
            }
        } catch (err) {
            console.error('Error saving staff:', err);
            const errorMessage = err.response?.data?.error?.message || 'Failed to save staff member';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleCredentialsClose = () => {
        setCredentialsModal(false);
        setTempCredentials(null);
        history.push('/admin/3pl-staff');
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    if (loading) {
        return (
            <div className="content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <div className="content">
            <Row>
                <Col md="12">
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h4" style={{ color: colors.blue }}>
                                {isEditMode ? 'Edit Staff Member' : 'Add New Staff Member'}
                            </CardTitle>
                            <p style={{ color: colors.muted, marginBottom: 0 }}>
                                {isEditMode ? 'Update staff details and permissions' : 'Create a new team member with specific access permissions'}
                            </p>
                        </CardHeader>
                        <CardBody>
                            {error && <Alert color="danger">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                {/* Personal Information */}
                                <h5 style={{ color: colors.blue, marginBottom: '15px', borderBottom: `2px solid ${colors.blue}`, paddingBottom: '10px' }}>
                                    Personal Information
                                </h5>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>First Name *</Label>
                                            <Input
                                                type="text"
                                                value={formData.firstName}
                                                onChange={(e) => handleChange('firstName', e.target.value)}
                                                placeholder="Enter first name"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Last Name *</Label>
                                            <Input
                                                type="text"
                                                value={formData.lastName}
                                                onChange={(e) => handleChange('lastName', e.target.value)}
                                                placeholder="Enter last name"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Email *</Label>
                                            <Input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => handleChange('email', e.target.value)}
                                                placeholder="staff@company.com"
                                                required
                                                disabled={isEditMode} // Can't change email in edit mode
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Phone</Label>
                                            <Input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => handleChange('phone', e.target.value)}
                                                placeholder="+256..."
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Role / Title</Label>
                                            <Input
                                                type="text"
                                                value={formData.role}
                                                onChange={(e) => handleChange('role', e.target.value)}
                                                placeholder="e.g., Operations Manager, Finance Officer"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{isEditMode ? 'New Password (leave blank to keep current)' : 'Password (optional — leave blank to email them an invitation to set their own)'}</Label>
                                            <Input
                                                type="password"
                                                value={formData.password}
                                                onChange={(e) => handleChange('password', e.target.value)}
                                                placeholder={isEditMode ? 'Leave blank to keep current password' : 'Min 8 characters (or leave blank)'}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {/* Permissions */}
                                <h5 style={{ color: colors.blue, marginTop: '30px', marginBottom: '15px', borderBottom: `2px solid ${colors.blue}`, paddingBottom: '10px' }}>
                                    Access Permissions
                                    <PartnerHelp topic="staffPermissions" />
                                </h5>
                                <p style={{ color: colors.muted, marginBottom: '20px' }}>
                                    Define what this staff member can access and manage
                                </p>

                                <Row>
                                    {Object.entries(permissionDescriptions).map(([key, config]) => (
                                        <Col md="6" key={key}>
                                            <Card style={{ marginBottom: '15px', border: `1px solid ${colors.line}` }}>
                                                <CardBody style={{ padding: '15px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                            <i className={config.icon} style={{ fontSize: '20px', color: colors.blue, marginTop: '2px' }} />
                                                            <div>
                                                                <strong style={{ fontSize: '14px' }}>{config.label}</strong>
                                                                <p style={{ fontSize: '12px', color: colors.muted, marginBottom: 0, marginTop: '4px' }}>
                                                                    {config.desc}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <Input
                                                            type="select"
                                                            value={formData.permissions[key]}
                                                            onChange={(e) => handlePermissionChange(key, e.target.value)}
                                                            style={{ width: '120px', fontSize: '14px' }}
                                                        >
                                                            <option value="none">No Access</option>
                                                            <option value="view">View Only</option>
                                                            <option value="full">Full Access</option>
                                                        </Input>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>

                                {/* Submit Buttons */}
                                <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
                                    <Button
                                        type="submit"
                                        style={{ backgroundColor: colors.green, borderColor: colors.green }}
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner size="sm" /> Saving...
                                            </>
                                        ) : (
                                            isEditMode ? 'Update Staff' : 'Create Staff'
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        onClick={() => history.push('/admin/3pl-staff')}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Credentials Modal */}
            <Modal isOpen={credentialsModal} toggle={handleCredentialsClose} backdrop="static">
                <ModalHeader style={{ backgroundColor: colors.green, color: colors.white }}>
                    ✅ Staff Member Created Successfully
                </ModalHeader>
                <ModalBody>
                    <Alert color="warning">
                        <strong>Important!</strong> Please share these credentials securely with the staff member.
                        The password cannot be retrieved later.
                    </Alert>

                    <div style={{ backgroundColor: colors.lineSoft, padding: '20px', borderRadius: '8px', marginTop: '15px' }}>
                        <FormGroup>
                            <Label style={{ fontWeight: 'bold' }}>Email</Label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <Input value={tempCredentials?.email || ''} readOnly />
                                <Button color="secondary" onClick={() => copyToClipboard(tempCredentials?.email)}>
                                    Copy
                                </Button>
                            </div>
                        </FormGroup>
                        <FormGroup style={{ marginTop: '15px', marginBottom: 0 }}>
                            <Label style={{ fontWeight: 'bold' }}>Temporary Password</Label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <Input value={tempCredentials?.password || ''} readOnly />
                                <Button color="secondary" onClick={() => copyToClipboard(tempCredentials?.password)}>
                                    Copy
                                </Button>
                            </div>
                        </FormGroup>
                    </div>

                    <p style={{ marginTop: '15px', fontSize: '14px', color: colors.muted }}>
                        The staff member can log in at the same login page using these credentials.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button style={{ backgroundColor: colors.blue, borderColor: colors.blue }} onClick={handleCredentialsClose}>
                        Done
                    </Button>
                </ModalFooter>
            </Modal>
        </div >
    );
};

export default CreateStaff;
